import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCms } from '../contexts/CmsContext';
import { FaMoneyBillWave, FaChartLine, FaGlobe, FaUsers } from 'react-icons/fa';
import Header from './Header';
import Footer from './Footer';
import ScrollAnimation from './ScrollAnimation';

const Career = () => {
  const { language } = useLanguage();
  const { getContent } = useCms();
  const t = getContent(language);
  const c = t.careerPage;
  const [selectedFileName, setSelectedFileName] = React.useState('');
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    gender: '',
    email: '',
    resume: null
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFileName(file.name);
      setFormData(prev => ({
        ...prev,
        resume: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      data.append(key, formData[key]);
    });

    try {
      const apiUrl = '/backend/career.php';

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: data
      });

      const result = await response.json();

      if (result.success) {
        alert(c.applicationForm.successMessage || (language === 'ar' ? 'تم إرسال طلبك بنجاح!' : 'Application submitted successfully!'));
        setFormData({
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          phoneNumber: '',
          gender: '',
          email: '',
          resume: null
        });
        setSelectedFileName('');
        // Reset file input value
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
      } else {
        alert(result.error || (language === 'ar' ? 'حدث خطأ أثناء الإرسال.' : 'Error submitting application.'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert(language === 'ar' ? 'حدث خطأ في الاتصال.' : 'Connection error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileButtonText =
    c.applicationForm.fileUploadButton || (language === 'ar' ? 'اختر ملفاً' : 'Choose File');
  const filePlaceholderText =
    c.applicationForm.fileUploadPlaceholder || (language === 'ar' ? 'لم يتم اختيار ملف' : 'No file chosen');

  const inputClass = `w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary ${language === 'ar' ? 'text-right' : 'text-left'}`;
  
  const inputStyle = {
    textAlign: language === 'ar' ? 'right' : 'left',
    direction: language === 'ar' ? 'rtl' : 'ltr'
  };

  return (
    <div
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      className={`${
        language === 'ar' ? 'font-cairo rtl' : 'font-sans ltr'
      } bg-background-light dark:bg-background-dark text-slate-900 dark:text-slate-100 transition-colors duration-300 animate-fade-in`}
    >
      {language === 'ar' && (
        <style>{`
          input[type="date"] { 
            direction: ltr !important;
            text-align: right !important;
            display: flex !important;
          }
          input[type="date"]::-webkit-datetime-edit { 
            margin-left: auto !important;
            margin-right: 0 !important;
          }
          input[type="date"]::-webkit-calendar-picker-indicator { 
            position: absolute; 
            left: 1rem; 
            right: auto; 
          }
        `}</style>
      )}
      <Header />
      <main>
        <section className="relative min-h-[550px] md:min-h-[450px] md:h-[60vh] flex items-start md:items-center justify-center overflow-hidden pt-32 md:pt-0">
          <img
            alt="Diverse team of engineers"
            className="absolute inset-0 w-full h-full object-cover"
            src={c.hero.image}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
          <div className="relative z-10 w-full max-w-[1440px] px-6 lg:px-12 pb-10 md:pb-0">
            <ScrollAnimation animation="fade-up" className={`max-w-4xl ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <span className="inline-block text-secondary text-xs font-bold tracking-[0.3em] uppercase mb-4">
                {c.hero.badge}
              </span>
              <h1 className="text-white text-4xl md:text-6xl font-black mb-6 leading-tight">
                {c.hero.titleLine1} <br />
                <span className="text-secondary">{c.hero.titleEmphasis}</span> {c.hero.titleLine2}
              </h1>
              <p className="text-white/80 text-base md:text-lg font-light max-w-2xl leading-relaxed">
                {c.hero.subtitle}
              </p>
            </ScrollAnimation>
          </div>
        </section>

        <section className="py-24 bg-white dark:bg-background-dark">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <ScrollAnimation animation="fade-up" className="mb-16 max-w-3xl">
              <h2 className="text-sm font-extrabold text-primary dark:text-accent uppercase tracking-[0.4em] mb-4">
                {c.culture.badge}
              </h2>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-tight">
                {c.culture.title}
              </h3>
              <p className="mt-6 text-slate-600 dark:text-slate-400 text-lg">
                {c.culture.description}
              </p>
            </ScrollAnimation>
            <ScrollAnimation animation="fade-up" delay={200} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="relative rounded-3xl overflow-hidden group h-[400px]">
                <img
                  alt="Team Collaboration"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  src={c.culture.cards.collaboration.image}
                />
                <div className="absolute inset-0 culture-card-overlay flex flex-col justify-end p-8">
                  <h5 className="text-white font-bold text-xl mb-2">{c.culture.cards.collaboration.title}</h5>
                  <p className="text-white/70 text-sm">{c.culture.cards.collaboration.text}</p>
                </div>
              </div>
              <div className="relative rounded-3xl overflow-hidden group h-[400px]">
                <img
                  alt="Professional Development"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  src={c.culture.cards.workshops.image}
                />
                <div className="absolute inset-0 culture-card-overlay flex flex-col justify-end p-8">
                  <h5 className="text-white font-bold text-xl mb-2">{c.culture.cards.workshops.title}</h5>
                  <p className="text-white/70 text-sm">{c.culture.cards.workshops.text}</p>
                </div>
              </div>
              <div className="relative rounded-3xl overflow-hidden group h-[400px]">
                <img
                  alt="Site Visits"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  src={c.culture.cards.impact.image}
                />
                <div className="absolute inset-0 culture-card-overlay flex flex-col justify-end p-8">
                  <h5 className="text-white font-bold text-xl mb-2">{c.culture.cards.impact.title}</h5>
                  <p className="text-white/70 text-sm">{c.culture.cards.impact.text}</p>
                </div>
              </div>
              <div className="relative rounded-3xl overflow-hidden group h-[400px]">
                <img
                  alt="Saudi Vision 2030 Alignment"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  src={c.culture.cards.vision.image}
                />
                <div className="absolute inset-0 culture-card-overlay flex flex-col justify-end p-8">
                  <h5 className="text-white font-bold text-xl mb-2">{c.culture.cards.vision.title}</h5>
                  <p className="text-white/70 text-sm">{c.culture.cards.vision.text}</p>
                </div>
              </div>
            </ScrollAnimation>
          </div>
        </section>

        <section className="py-24 bg-slate-50 dark:bg-slate-900/30">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <ScrollAnimation animation="fade-up" className="grid lg:grid-cols-4 gap-8">
              <div className="bg-white dark:bg-slate-800 p-10 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-700 hover:border-secondary transition-all">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                  <FaMoneyBillWave className="text-primary text-3xl" />
                </div>
                <h4 className="text-xl font-bold mb-4">{c.benefits.items.compensation.title}</h4>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  {c.benefits.items.compensation.text}
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-10 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-700 hover:border-secondary transition-all">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                  <FaChartLine className="text-primary text-3xl" />
                </div>
                <h4 className="text-xl font-bold mb-4">{c.benefits.items.growth.title}</h4>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  {c.benefits.items.growth.text}
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-10 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-700 hover:border-secondary transition-all">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                  <FaGlobe className="text-primary text-3xl" />
                </div>
                <h4 className="text-xl font-bold mb-4">{c.benefits.items.impact.title}</h4>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  {c.benefits.items.impact.text}
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-10 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-700 hover:border-secondary transition-all">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                  <FaUsers className="text-primary text-3xl" />
                </div>
                <h4 className="text-xl font-bold mb-4">{c.benefits.items.culture.title}</h4>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  {c.benefits.items.culture.text}
                </p>
              </div>
            </ScrollAnimation>
          </div>
        </section>

        <section className="py-12 md:py-24 bg-slate-50 dark:bg-slate-900/50" id="open-positions">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">


            {/* General Application CTA */}
            <ScrollAnimation animation="fade-up" className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2">{c.cta.title}</h3>
                <p className="text-slate-500">{c.cta.text}</p>
            </ScrollAnimation>

            <ScrollAnimation animation="fade-up" delay={200} className="rounded-2xl border border-slate-200 dark:border-slate-700 shadow-lg bg-slate-900 text-slate-100">
              <form className="p-5 md:p-10 space-y-8 bg-[rgb(26,52,36)]" onSubmit={handleSubmit}>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {c.applicationForm.firstName} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      style={inputStyle}
                      placeholder={c.applicationForm.firstNamePlaceholder}
                      dir={language === 'ar' ? 'rtl' : 'ltr'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {c.applicationForm.lastName} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      style={inputStyle}
                      placeholder={c.applicationForm.lastNamePlaceholder}
                      dir={language === 'ar' ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {c.applicationForm.dateOfBirth} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type={language === 'ar' && !formData.dateOfBirth ? "text" : "date"}
                      onFocus={(e) => (e.target.type = "date")}
                      onBlur={(e) => {
                        if (!e.target.value && language === 'ar') e.target.type = "text";
                      }}
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      style={inputStyle}
                      placeholder={c.applicationForm.dateOfBirthPlaceholder}
                      dir={language === 'ar' ? 'rtl' : 'ltr'}
                      lang={language === 'ar' ? 'ar' : 'en'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {c.applicationForm.phoneNumber} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      style={inputStyle}
                      placeholder={c.applicationForm.phoneNumberPlaceholder}
                      dir={language === 'ar' ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <span className="block text-sm font-medium mb-3">
                      {c.applicationForm.gender} <span className="text-red-500">*</span>
                    </span>
                    <div className="flex flex-col sm:flex-row gap-4 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name="gender"
                          value="male"
                          checked={formData.gender === 'male'}
                          onChange={handleChange}
                          className="text-secondary"
                          required
                        />
                        <span>{c.applicationForm.genderOptions.male}</span>
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name="gender"
                          value="female"
                          checked={formData.gender === 'female'}
                          onChange={handleChange}
                          className="text-secondary"
                          required
                        />
                        <span>{c.applicationForm.genderOptions.female}</span>
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name="gender"
                          value="preferNotToSay"
                          checked={formData.gender === 'preferNotToSay'}
                          onChange={handleChange}
                          className="text-secondary"
                          required
                        />
                        <span>{c.applicationForm.genderOptions.preferNotToSay}</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {c.applicationForm.email} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      style={inputStyle}
                      placeholder={c.applicationForm.emailPlaceholder}
                      dir={language === 'ar' ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 items-end">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {c.applicationForm.fileUpload} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-4">
                      <label className="relative inline-flex items-center">
                        <span className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-secondary text-slate-900 text-sm font-semibold hover:bg-secondary/90 cursor-pointer">
                          {fileButtonText}
                        </span>
                        <input
                          type="file"
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          onChange={handleFileChange}
                          required
                        />
                      </label>
                      <span className="text-xs text-slate-200 truncate max-w-[200px]">
                        {selectedFileName || filePlaceholderText}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                     <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`px-8 py-3 rounded-lg bg-secondary text-slate-900 font-bold hover:bg-secondary/90 transition-all ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {isSubmitting ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : (language === 'ar' ? 'إرسال الطلب' : 'Submit Application')}
                      </button>
                  </div>
                </div>
              </form>
            </ScrollAnimation>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Career;
